"use client";

import React, { createContext, useContext, useMemo } from "react";
import en from "@/content/en.json";
import { LOCALE_CONFIG } from "@/lib/constants";

type Content = typeof en;
export type Language = (typeof LOCALE_CONFIG.SUPPORTED)[number];

interface LanguageContextType {
  language: Language;
  content: Content;
  rawContent: Content;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const HTML_SKIP_KEYS = new Set(["pgp_key"]);

const parseContentWithHtml = (data: unknown, key?: string): unknown => {
  if (typeof data === "string") {
    if (key && HTML_SKIP_KEYS.has(key)) {
      return data;
    }

    const hasHtml = /<[a-z][\s\S]*>/i.test(data);

    if (hasHtml) {
      return <span key={data} dangerouslySetInnerHTML={{ __html: data }} />;
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => parseContentWithHtml(item));
  }

  if (typeof data === "object" && data !== null) {
    const result: Record<string, unknown> = {};
    for (const childKey in data) {
      result[childKey] = parseContentWithHtml(
        (data as Record<string, unknown>)[childKey],
        childKey,
      );
    }
    return result;
  }

  return data;
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const processedContent = useMemo(() => parseContentWithHtml(en) as Content, []);

  const value = {
    language: LOCALE_CONFIG.DEFAULT,
    content: processedContent,
    rawContent: en,
    setLanguage: () => {},
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
